import {
  PSYCH_CARE_REHAB_SPECIALIZATIONS,
  REHABILITATION_PAYMENT_OPTION,
  SEARCH_TYPES,
  SOLUTION_HOME_CARE,
  SOLUTION_MEDICAL_SUPPLIES,
} from "core/consts";
import {
  isCareSearch,
  isPsychCareSearch,
  isRehabSearch,
  matchesWhitelistScopes,
} from "core/model/auctions";
import { defaultSearchType } from "core/model/careseekers";
import {
  hasPsychCareRehabSolution,
  showMultipleSpecializations,
  showSpecializations,
} from "core/model/patients";
import {
  convertBirthDateIn,
  convertBirthDateOut,
} from "core/model/utils/dates";
import {
  Auction,
  BarthelAssessment,
  GetOntologiesType,
  GetOntologyType,
  SearchType,
} from "core/types";
import {
  fromUnixTime,
  getUnixTime,
  isBefore,
  isSameDay,
  isValid,
  startOfDay,
} from "date-fns";
import {
  convertOutPayers,
  createGuardiansModelDefinition,
  createRelativesModelDefinition,
  validatePayersInsurance,
} from "dsl/ecosystems/PatientAssessment/sharedModelDefinitions";
import { Iterable, List, fromJS } from "immutable";
import {
  ConversionModel,
  activableInputModelDefinition,
  algoliaReactSelectDef,
  convertEncryptedFieldOut,
  convertModelDefinition,
  encryptedDef,
  phoneNumberModel,
  toJS,
  valueDef,
} from "react-forms-state";

export function defaultSolutionsForSearchType(searchType: SearchType) {
  if (searchType == SEARCH_TYPES.MEDICAL_SUPPLIES)
    return [SOLUTION_MEDICAL_SUPPLIES];
  if (searchType == SEARCH_TYPES.HOME_CARE) return [SOLUTION_HOME_CARE];
  return null;
}

export { defaultSearchType };

const GP_FIELDS = [
  { fieldName: "general_practitioner_city", propertyKey: "city" },
  { fieldName: "general_practitioner_first_name", propertyKey: "first_name" },
  {
    fieldName: "general_practitioner_honorific_suffix",
    propertyKey: "honorific_suffix",
  },
  {
    fieldName: "general_practitioner_honorific_prefix",
    propertyKey: "honorific_prefix",
  },
  {
    fieldName: "general_practitioner_house_number",
    propertyKey: "house_number",
  },
  { fieldName: "general_practitioner_job_title", propertyKey: "job_title" },
  { fieldName: "general_practitioner_last_name", propertyKey: "last_name" },
  { fieldName: "general_practitioner_phone", propertyKey: "phone" },
  { fieldName: "general_practitioner_street", propertyKey: "street" },
  { fieldName: "general_practitioner_zipcode", propertyKey: "zipcode" },
] as const;

const PSYCHIATRIST_FIELDS = [
  { fieldName: "psychiatrist_city", propertyKey: "city" },
  { fieldName: "psychiatrist_first_name", propertyKey: "first_name" },
  {
    fieldName: "psychiatrist_honorific_suffix",
    propertyKey: "honorific_suffix",
  },
  {
    fieldName: "psychiatrist_honorific_prefix",
    propertyKey: "honorific_prefix",
  },
  {
    fieldName: "psychiatrist_house_number",
    propertyKey: "house_number",
  },
  { fieldName: "psychiatrist_job_title", propertyKey: "job_title" },
  { fieldName: "psychiatrist_last_name", propertyKey: "last_name" },
  { fieldName: "psychiatrist_phone", propertyKey: "phone" },
  { fieldName: "psychiatrist_street", propertyKey: "street" },
  { fieldName: "psychiatrist_zipcode", propertyKey: "zipcode" },
] as const;

const createGeneralPractitionerModelDefinition = () => {
  const fieldDefinitions = GP_FIELDS.reduce(
    (acc, { fieldName, propertyKey }) => ({
      ...acc,
      ...encryptedDef(propertyKey, { out_name: fieldName }),
    }),
    {},
  );

  return {
    ...valueDef("name", {
      out_name: "general_practitioner_name",
    }),
    ...valueDef("contact_details", {
      out_name: "general_practitioner_contact_details",
    }),
    ...fieldDefinitions,
    convertOut: (_data: any, _props?: any, globalValue?: any) => {
      const encryptedFields = GP_FIELDS.reduce(
        (acc, { fieldName, propertyKey }) => {
          const value = convertEncryptedFieldOut(
            globalValue?.getIn?.([fieldName]),
          );
          if (value) {
            acc[propertyKey] = value;
          }
          return acc;
        },
        {} as Record<string, any>,
      );

      const hasAnyData = Object.keys(encryptedFields).length > 0;

      if (!hasAnyData) {
        return null;
      }

      return {
        ...encryptedFields,
      };
    },
  };
};

const createPsychiatristModelDefinition = () => {
  const fieldDefinitions = PSYCHIATRIST_FIELDS.reduce(
    (acc, { fieldName, propertyKey }) => ({
      ...acc,
      ...encryptedDef(propertyKey, {
        convertOut: (value: any, props?: any) => {
          const searchType = props?.formInputValue?.search_type;
          if (!isPsychCareSearch(searchType)) {
            return undefined;
          }
          return convertEncryptedFieldOut(value);
        },
        out_name: fieldName,
      }),
    }),
    {},
  );

  return {
    ...fieldDefinitions,
    convertOut: (_data: any, props?: any, globalValue?: any) => {
      const searchType =
        props?.formInputValue?.search_type ??
        (globalValue
          ? toJS<{ search_type?: SearchType }>(globalValue).search_type
          : undefined);

      if (!isPsychCareSearch(searchType)) {
        return undefined;
      }

      const encryptedFields = PSYCHIATRIST_FIELDS.reduce(
        (acc, { fieldName, propertyKey }) => {
          const value = convertEncryptedFieldOut(
            globalValue?.getIn?.([fieldName]),
          );
          if (value) {
            acc[propertyKey] = value;
          }
          return acc;
        },
        {} as Record<string, any>,
      );

      const hasAnyData = Object.keys(encryptedFields).length > 0;

      if (!hasAnyData) {
        return null;
      }

      return {
        ...encryptedFields,
      };
    },
  };
};

const createCareNeedsActivableField = (fieldName: string) => {
  const base: any = activableInputModelDefinition(
    `patient.care_needs.${fieldName}`,
    fieldName,
    {
      ...valueDef("description", {
        convertOut: () => undefined,
        out_name: `${fieldName}_description`,
      }),
    },
  );

  return {
    ...base,
    [`_${fieldName}`]: {
      ...base[`_${fieldName}`],
      convertOut: () => undefined,
    },
    ...valueDef(`${fieldName}_description`, {
      convertIn: (_value: any, _props: any, allValues: any) =>
        allValues?.getIn?.([
          "patient",
          "care_needs",
          fieldName,
          "description",
        ]) ?? null,
      convertOut: () => undefined,
    }),
    ...valueDef(fieldName, {
      convertOut: (value: any, _props: any, globalValue: any) => {
        const placeholderChecked = globalValue?.getIn?.([`_${fieldName}`]);
        if (!placeholderChecked) return null;

        const description = globalValue?.getIn?.([`${fieldName}_description`]);
        const descJS = Iterable.isIterable(description)
          ? description.toJS()
          : description;
        return { description: descJS ?? "" };
      },
    }),
  };
};

const model: ConversionModel = {
  ...valueDef("id"),
  profile: {
    ...valueDef("search_location"),
  },
  ...valueDef("start_date_flexible", {
    show: matchesWhitelistScopes([
      {
        country: "DE",
        search_type: SEARCH_TYPES.CARE,
      },
      {
        country: "DE",
        search_type: SEARCH_TYPES.HOSPITAL,
      },
    ]),
  }),
  ...valueDef("start_date", {
    defaultValue: () => {
      return getUnixTime(startOfDay(new Date()));
    },
    validate: (value, props) => {
      const { formInputValue, translations } = props;
      // date can't be empty
      if (!value) {
        return {
          customMessage:
            translations.assessments.formValidation.startDateMissing,
        };
      }

      // Calendar-day "before today" in local time. Don't use isPastDate(..., -1): it mixes UTC
      // addDays + isSameDay and flags start-of-today unix as "past" in positive-offset zones.
      const selected = fromUnixTime(value);
      if (!isValid(selected)) {
        return {
          customMessage: translations.general.errorDateInPast,
        };
      }
      const isCalendarBeforeToday = isBefore(
        startOfDay(selected),
        startOfDay(new Date()),
      );
      // on initial creation of the patient, formInput is undefined, users can't enter date in past
      // on edit, formInput is filled, users can go to next step with same date, on change it can't be in the past either
      if (
        (!formInputValue && isCalendarBeforeToday) ||
        (formInputValue &&
          isCalendarBeforeToday &&
          !isSameDay(fromUnixTime(formInputValue.start_date), selected))
      ) {
        return {
          customMessage: translations.general.errorDateInPast,
        };
      }

      return true;
    },
  }),
  ...valueDef("search_type", {
    defaultValue: (props: AnyObject) => defaultSearchType(props.careseeker),
    validate(value, { translations }) {
      return value
        ? true
        : {
            customMessage:
              translations.assessments.formValidation.singleSelectError,
          };
    },
  }),
  ...valueDef("assessment_variant"),
  ...valueDef("solutions", {
    convertIn: (value: List<number> | undefined) => {
      if (!value) return null;
      return value;
    },
    convertOut: (value: List<number> | number) => {
      // Transport searches are limited to a single solution.
      // We therefore use a radio selector for the selection
      // which outputs a single solution.
      const solutions = toJS<number[] | number>(value);

      return Array.isArray(solutions) ? solutions : [solutions];
    },
    defaultValue: (props: AnyObject) => {
      const searchType = defaultSearchType(props.careseeker);
      if (searchType) return defaultSolutionsForSearchType(searchType);
      return undefined;
    },
    errorString: (t) => t.actions.missing,
    fieldRequired: true,
    validate: (value: List<number> | number | undefined, { translations }) => {
      const solutions = toJS<number[] | number | undefined>(value);

      const hasSelection = Boolean(
        Array.isArray(solutions) ? solutions?.length : solutions,
      );

      if (!hasSelection) {
        return {
          customMessage:
            translations.assessments.formValidation.typeOfCareMissing,
        };
      }

      return true;
    },
  }),
  ...valueDef("specializations", {
    convertIn: (
      value: any,
      props: {
        formInputValue: AnyObject | null;
        getOntologies: GetOntologiesType;
        getOntology: GetOntologyType;
      },
    ) => {
      const { formInputValue, getOntologies } = props;
      const inputValue = Iterable.isIterable(value) ? value.toJS() : value;
      const searchType = formInputValue?.search_type;
      const showMultiple = showMultipleSpecializations({
        searchType,
      });

      const solutions = toJS<number[]>(formInputValue?.solutions);

      const shouldAllowSpecializations = showSpecializations({
        searchType,
        solutions,
      });

      // Handle both array and single value inputs (radio button returns single value)
      const inputArray = [inputValue ?? []].flat();

      if (!shouldAllowSpecializations || inputArray.length === 0) {
        return null;
      }

      const ontologyValues = getOntologies({
        sort: true,
        type: "specializations",
      });

      const isPsychCareWithRehab =
        isPsychCareSearch(searchType) && hasPsychCareRehabSolution(solutions);

      if (isPsychCareWithRehab) {
        const filteredValues = ontologyValues.filter((ontVal) =>
          PSYCH_CARE_REHAB_SPECIALIZATIONS.includes(ontVal.value),
        );
        const specializations = filteredValues
          .filter((ontVal) => inputArray.includes(ontVal.value))
          .map(({ name: label, value }) => ({ label, value }));
        return showMultiple
          ? specializations
          : specializations[0]?.value ?? null;
      }

      const specializations = ontologyValues
        .filter((ontVal) => inputArray.includes(ontVal.value))
        .map(({ name: label, value }) => ({ label, value }));

      return showMultiple ? specializations : specializations[0]?.value ?? null;
    },
    convertOut: (value: any) => {
      const outputValue = Iterable.isIterable(value) ? value.toJS() : value;

      if (!outputValue) {
        return undefined;
      }

      const extractValue = (item: any): number | null => {
        const val = item && typeof item === "object" ? item.value : item;
        return typeof val === "number" ? val : null;
      };

      // 1. Normalize the input into a single flat array
      const rawItems = [outputValue?.value ?? outputValue].flat();

      // 2. Extract and filter in one pass
      const values = rawItems
        .map(extractValue)
        .filter((v): v is number => v !== null);

      // 3. Return result or undefined
      return values.length > 0 ? values : undefined;
    },
  }),
  ...valueDef("patient.id"),
  ...valueDef("patient.careseeker.id", {
    convertOut: (_, props) => {
      return props?.careseeker?.id;
    },
  }),
  ...valueDef("patient.profile.discharge_date", {
    convertOut(value, props, formOutputValue) {
      const formOutput = toJS<{ search_type: SearchType }>(formOutputValue);
      if (!isRehabSearch(formOutput.search_type)) {
        return null;
      }
      return value;
    },
    defaultValue: () => {
      return getUnixTime(new Date());
    },
    out_name: "discharge_date",
  }),
  patient: {
    ...valueDef("version"),
    ...valueDef("kis_import", { defaultValue: false }),
    ...valueDef("_kis_helper_element", {
      convertOut: () => undefined,
      defaultValue: () => null,
    }),
    ...valueDef("external_id"),
    ...valueDef("station_full_id", {
      convertIn: (input, _, immutableInput) => {
        const oldStation = immutableInput?.get("station");
        return input || { label: oldStation };
      },
      convertOut: (output) => {
        if (Number.isInteger(output)) return output;
        const outputValue = Iterable.isIterable(output)
          ? output.toJS()
          : output;
        return outputValue?.id ?? null;
      },
    }),
    care_needs: {
      ...createCareNeedsActivableField("integration_assistance"),
      ...createCareNeedsActivableField("psychiatric_home_nursing"),
      ...createCareNeedsActivableField("substitute_offers"),
      ...createCareNeedsActivableField("sociotherapy"),
      ...createCareNeedsActivableField("daily_activities"),
      ...createCareNeedsActivableField("psych_protected_area"),
      ...createCareNeedsActivableField("psych_closed_area"),
    },
    // Primarily KIS fields
    diagnosis: {
      ...valueDef("main_diagnosis"),
      ...valueDef("secondary_diagnosis"),
      ...valueDef("hl7_main_diagnoses"),
      ...valueDef("hl7_secondary_diagnoses"),
      ...valueDef("allergies"),
      ...valueDef("medical_history"),
      dependency_diagnosis: {
        ...valueDef("barthel_assessment", {
          convertOut(valueMap) {
            const value = toJS<BarthelAssessment | null>(valueMap);
            if (!value) return null;

            return {
              ...value,
              form:
                value.form &&
                Object.values(value.form).some(
                  (value) => typeof value === "number",
                )
                  ? value.form
                  : null,
            };
          },
        }),
        ...valueDef("early_rehab_assessment", {
          convertOut(valueMap) {
            const value = toJS<BarthelAssessment | null>(valueMap);
            if (!value) return null;

            return {
              ...value,
              form:
                value.form &&
                Object.values(value.form).some(
                  (value) => typeof value === "number",
                )
                  ? value.form
                  : null,
            };
          },
        }),
        ...valueDef("singer_assessment", {
          convertOut(valueMap) {
            const value = toJS<BarthelAssessment | null>(valueMap);
            if (!value) return null;

            return {
              ...value,
              form:
                value.form &&
                Object.values(value.form).some(
                  (value) => typeof value === "number",
                )
                  ? value.form
                  : null,
            };
          },
        }),
      },
      hospital_stay: {
        ...valueDef("operation_date"),
        ...valueDef("operation_description"),
        ...valueDef("doctor_in_charge_in_hospital"),
        ...valueDef("doctor_in_charge_in_hospital_phone", {
          ...phoneNumberModel(false),
          defaultValue: (props: any) => {
            const stationInformation = props.careseeker?.stations_full;
            return stationInformation?.length === 1
              ? stationInformation[0].phone_number
              : "";
          },
        }),
      },
      ...valueDef("infection_and_germs"),
      ...valueDef("infection_and_germs_state"),
    },
    profile: {
      financing: {
        ...encryptedDef("insurance_number"),
        ...valueDef("insurance_id"),
        ...valueDef("insurance"),
        carelevel: {
          out: "carelevel",
          ...valueDef("level"),
        },
      },
      ...valueDef("gender"),
      ...encryptedDef("first_name"),
      ...encryptedDef("last_name"),
      ...encryptedDef("height"),
      ...encryptedDef("weight"),
      ...encryptedDef("birth_date", {
        convertIn: convertBirthDateIn,
        convertOut: convertBirthDateOut,
      }),
      communication: {
        ...valueDef("has_guardian"),
        ...encryptedDef("guardian_contact_information"),
        ...valueDef("guardian_requested"),
        ...valueDef("relatives_available"),
        ...encryptedDef("relatives_description"),
        ...valueDef("patient_is_contact"),
        ...encryptedDef("patient_is_contact_description"),
        guardians: createGuardiansModelDefinition(),
        relatives: createRelativesModelDefinition(),
      },
      general_practitioner: createGeneralPractitionerModelDefinition(),
      placement_order: {
        out_path: "patient.profile.placement_order",
        ...valueDef("status", {
          convertIn: (
            value: number | null | undefined,
            _props,
            globalValue,
          ) => {
            // Backend sends nested object: { status: 1, type: 1, exhibiting_state: "..." }
            const placementOrder = globalValue?.getIn?.([
              "patient",
              "profile",
              "placement_order",
            ]);
            if (placementOrder) {
              const placementOrderJS = Iterable.isIterable(placementOrder)
                ? placementOrder.toJS()
                : placementOrder;
              return placementOrderJS?.status ?? null;
            }
            return value ?? null;
          },
          convertOut: (_value, _props, _globalValue) => {
            // This is handled in parent convertOut to create nested structure
            return undefined;
          },
        }),
        ...valueDef("type", {
          convertIn: (
            value: number | null | undefined,
            _props,
            globalValue,
          ) => {
            const placementOrder = globalValue?.getIn?.([
              "patient",
              "profile",
              "placement_order",
            ]);
            if (placementOrder) {
              const placementOrderJS = Iterable.isIterable(placementOrder)
                ? placementOrder.toJS()
                : placementOrder;
              return placementOrderJS?.type ?? null;
            }
            return value ?? null;
          },
          convertOut: (_value, _props, _globalValue) => {
            // This is handled in parent convertOut to create nested structure
            return undefined;
          },
        }),
        ...valueDef("exhibiting_state", {
          convertIn: (
            value: string | null | undefined,
            _props,
            globalValue,
          ) => {
            const placementOrder = globalValue?.getIn?.([
              "patient",
              "profile",
              "placement_order",
            ]);
            if (placementOrder) {
              const placementOrderJS = Iterable.isIterable(placementOrder)
                ? placementOrder.toJS()
                : placementOrder;
              return placementOrderJS?.exhibiting_state ?? null;
            }
            return value ?? null;
          },
          convertOut: (_value, _props, _globalValue) => {
            // This is handled in parent convertOut to create nested structure
            return undefined;
          },
        }),
        convertOut: (_data: any, _props?: any, globalValue?: any) => {
          // Read child values from form state
          const status = globalValue?.getIn?.([
            "patient",
            "profile",
            "placement_order",
            "status",
          ]);
          const type = globalValue?.getIn?.([
            "patient",
            "profile",
            "placement_order",
            "type",
          ]);
          const exhibitingState = globalValue?.getIn?.([
            "patient",
            "profile",
            "placement_order",
            "exhibiting_state",
          ]);

          const statusValue = Iterable.isIterable(status)
            ? status.toJS()
            : status;
          const typeValue = Iterable.isIterable(type) ? type.toJS() : type;
          const exhibitingStateValue = Iterable.isIterable(exhibitingState)
            ? exhibitingState.toJS()
            : exhibitingState;

          // When status is null/undefined, return null to remove placement_order entirely
          // This matches the RHF schema transform behavior
          if (statusValue === null || statusValue === undefined) {
            return null;
          }

          // Construct nested object
          const result: {
            status: number;
            type?: number;
            exhibiting_state?: string;
          } = { status: statusValue };
          if (typeValue) result.type = typeValue;
          if (exhibitingStateValue)
            result.exhibiting_state = exhibitingStateValue;

          return result;
        },
      },
      psychiatrist: createPsychiatristModelDefinition(),
      // end KIS only fields

      ...valueDef("care_duration_in_days", {
        validate: (value, { translations }, globalProps) => {
          const { search_type } = toJS<Partial<Auction>>(globalProps);
          if (isCareSearch(search_type) || isPsychCareSearch(search_type)) {
            return value
              ? true
              : {
                  customMessage:
                    translations.assessments.formValidation.careDurationMissing,
                };
          }
          return true;
        },
      }),
      ...valueDef("interested_long_term_stay"),
      ...valueDef("direct_transfer_necessary"),
    },
    social_worker: {
      complex: true,
      convertIn: (socialWorker, props) => {
        if (socialWorker == null) return props?.account?.id;
        return socialWorker.getIn(["id"], null);
      },
      convertOut: (output) => {
        const data = Iterable.isIterable(output) ? output.toJS() : output;
        if (!data) return {};
        return { id: data.value || data };
      },
      default: (props: any) =>
        props.account ? fromJS({ id: props.account.id }) : fromJS({}),
      out: "social_worker_account_id",
    },
  },
  payers: {
    ...valueDef("selected_payment_method", {
      convertOut: (value, _props, globalValue) => {
        const formValue = Iterable.isIterable(globalValue)
          ? globalValue.toJS()
          : globalValue;
        // if insurance is added to the patient creation then the selected payment option is decided there
        if (formValue?.payers_insurance?.id != null)
          return REHABILITATION_PAYMENT_OPTION.INSURANCE;
        return value;
      },
    }),
    ...algoliaReactSelectDef("insurance", {
      convertOut: convertOutPayers,
      out_name: "payers_insurance",
      validate: validatePayersInsurance,
    }),
  },
};

export default convertModelDefinition(model);
